import { isCourseFree } from "@repo/shared";
import { useTranslation } from "react-i18next";

import { useAvailableCourses } from "~/api/queries/useAvailableCourses";
import { resolveDirectEnrollHref } from "~/utils/courseLinks";

import type { SupportedLanguages } from "@repo/shared";

/**
 * El cierre de la landing. Replanteo 2026-07-31:
 *
 * - Se cayó la segunda salida ("Pre-inscribirme a los que vienen −80%") y la línea de
 *   urgencia del 80%: la pre-inscripción está oculta en la Home, así que el cierre
 *   prometía un descuento que acá no se puede tomar. Queda UN solo CTA, que es lo que
 *   corresponde al final de la página.
 * - El mensaje ya no promete plazos ("en un mes…"): los cursos son asincrónicos y cada
 *   uno dura lo que dura. Ahora cierra con lo único cierto y chico: el primer paso.
 * - Las pruebas se acortaron y van `whitespace-nowrap` para que NUNCA se partan en dos
 *   líneas en desktop, que era como se venían viendo.
 */
export function FinalCtaLaunch() {
  const { t, i18n } = useTranslation();

  // El CTA lleva al alta real del curso gratis, igual que el hero y el curso destacado.
  const { data: courses } = useAvailableCourses({ language: i18n.language as SupportedLanguages });
  const featured = courses?.find((c) => c.isFeatured) ?? courses?.find(isCourseFree);
  const ctaHref = featured ? resolveDirectEnrollHref({ slug: featured.slug }) : "#cursos";

  return (
    // Desktop: el cierre + footer llenan el viewport completo al scrollear al fondo
    // (sin que asome la sección anterior): min-h = 100svh − footer (~330px), contenido
    // centrado verticalmente. En mobile el alto natural alcanza.
    <section className="landing-grain relative flex items-center bg-landing-cream/70 px-5 py-20 text-center backdrop-blur-[1px] sm:px-8 sm:py-24 lg:min-h-[calc(100svh-330px)]">
      <div className="relative z-10 mx-auto max-w-[820px]">
        <h2
          className="font-display text-[clamp(26px,3.8vw,42px)] font-extrabold leading-[1.1] tracking-tight text-landing-text"
          style={{ textWrap: "balance" }}
        >
          {t("landing.launch.fin.headlinePre")}
          <em className="not-italic text-landing-red">{t("landing.launch.fin.headlineEm")}</em>
          {t("landing.launch.fin.headlinePost")}
        </h2>
        <p className="mx-auto mt-4 max-w-[54ch] text-[15.5px] leading-relaxed text-landing-muted">
          {t("landing.launch.fin.lead")}
        </p>

        {/* w-fit + mx-auto: en mobile la lista se alinea a la izquierda (se lee mejor)
            pero el bloque queda centrado con el resto, no pegado al borde. */}
        {/* Acá había una tercera repetición de "gratis · sin saber tecnología · con
            certificado", que la página ya dijo en el hero, en el curso destacado y en la
            sección del certificado. Repetirlo por cuarta vez no convence a nadie. Es el
            último scroll antes de irse: lo que corresponde es bajar el costo de entrar,
            no volver a recitar el catálogo de beneficios. */}
        <p className="mx-auto mt-5 max-w-[46ch] text-pretty text-[16.5px] font-semibold leading-snug text-landing-text">
          {t("landing.launch.fin.kicker")}
        </p>

        <div className="mt-8">
          <a
            href={ctaHref}
            data-cuelume-press
            data-cuelume-release
            className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-landing-red px-8 py-4 text-[16px] font-bold text-white shadow-landing-red transition-all duration-150 hover:-translate-y-0.5 hover:bg-landing-red-hover hover:shadow-landing-red-hover sm:w-auto"
          >
            {t("landing.launch.fin.cta1")} →
          </a>
          <p className="mt-3 text-[13.5px] text-landing-muted">{t("landing.launch.fin.sub")}</p>
        </div>
      </div>
    </section>
  );
}
